/**
 * Enumerações, rótulos em pt-BR e regras puras de campanhas, conjuntos e anúncios (Fase 4),
 * conferidas na referência da Marketing API v26.0 em 18/10/2026 (Campaign, AdSet, Ad,
 * AdCreative e o edge `/copies`). Ficam aqui porque a interface, os schemas Zod e a API usam.
 */

import { METRIC_DEFS, metricColumnId, type MetricKey } from "./metric-defs.js";

export const CAMPAIGN_LEVELS = ["campaigns", "adsets", "ads"] as const;

/** Métricas visíveis por padrão na tabela de campanhas. */
const METRIC_DEFAULT_COLUMNS: readonly MetricKey[] = [
  "spendCents",
  "results",
  "costPerResultCents",
  "ctr",
  "cpcCents",
];
export type CampaignLevel = (typeof CAMPAIGN_LEVELS)[number];

export const CAMPAIGN_LEVEL_LABELS: Record<CampaignLevel, { singular: string; plural: string }> = {
  campaigns: { singular: "Campanha", plural: "Campanhas" },
  adsets: { singular: "Conjunto de anúncios", plural: "Conjuntos de anúncios" },
  ads: { singular: "Anúncio", plural: "Anúncios" },
};

/** Objetivos ODAX vigentes (os únicos aceitos na criação desde a v17). */
export const CAMPAIGN_OBJECTIVES = [
  "OUTCOME_SALES",
  "OUTCOME_LEADS",
  "OUTCOME_TRAFFIC",
  "OUTCOME_ENGAGEMENT",
  "OUTCOME_AWARENESS",
  "OUTCOME_APP_PROMOTION",
] as const;
export type CampaignObjective = (typeof CAMPAIGN_OBJECTIVES)[number];

const OBJECTIVE_LABELS: Record<string, string> = {
  OUTCOME_SALES: "Vendas",
  OUTCOME_LEADS: "Cadastros",
  OUTCOME_TRAFFIC: "Tráfego",
  OUTCOME_ENGAGEMENT: "Engajamento",
  OUTCOME_AWARENESS: "Reconhecimento",
  OUTCOME_APP_PROMOTION: "Promoção de app",
  // Objetivos antigos ainda devolvidos em campanhas criadas antes do ODAX.
  CONVERSIONS: "Conversões (antigo)",
  LINK_CLICKS: "Cliques no link (antigo)",
  LEAD_GENERATION: "Geração de cadastros (antigo)",
  MESSAGES: "Mensagens (antigo)",
  REACH: "Alcance (antigo)",
  BRAND_AWARENESS: "Reconhecimento da marca (antigo)",
  VIDEO_VIEWS: "Visualizações de vídeo (antigo)",
  POST_ENGAGEMENT: "Engajamento com a publicação (antigo)",
  PAGE_LIKES: "Curtidas na página (antigo)",
  APP_INSTALLS: "Instalações do app (antigo)",
  PRODUCT_CATALOG_SALES: "Vendas do catálogo (antigo)",
  STORE_VISITS: "Visitas à loja (antigo)",
  EVENT_RESPONSES: "Respostas a eventos (antigo)",
  LOCAL_AWARENESS: "Reconhecimento local (antigo)",
  OFFER_CLAIMS: "Resgates de oferta (antigo)",
};

export function objectiveLabel(objective: string | null | undefined): string {
  if (!objective) return "—";
  return OBJECTIVE_LABELS[objective] ?? objective;
}

/** Status configurado (`status` / `configured_status`). */
export const CONFIGURED_STATUSES = ["ACTIVE", "PAUSED", "DELETED", "ARCHIVED"] as const;
export type ConfiguredStatus = (typeof CONFIGURED_STATUSES)[number];

export const CONFIGURED_STATUS_LABELS: Record<ConfiguredStatus, string> = {
  ACTIVE: "Ativo",
  PAUSED: "Pausado",
  DELETED: "Excluído",
  ARCHIVED: "Arquivado",
};

/** Status de entrega (`effective_status`) de campanha, conjunto e anúncio, unidos. */
export const EFFECTIVE_STATUSES = [
  "ACTIVE",
  "PAUSED",
  "DELETED",
  "ARCHIVED",
  "IN_PROCESS",
  "WITH_ISSUES",
  "CAMPAIGN_PAUSED",
  "ADSET_PAUSED",
  "PENDING_REVIEW",
  "DISAPPROVED",
  "PREAPPROVED",
  "PENDING_BILLING_INFO",
] as const;
export type EffectiveStatus = (typeof EFFECTIVE_STATUSES)[number];

export type DeliveryTone = "ok" | "atencao" | "erro" | "neutro";

const EFFECTIVE_STATUS_INFO: Record<EffectiveStatus, { label: string; tone: DeliveryTone }> = {
  ACTIVE: { label: "Veiculando", tone: "ok" },
  PAUSED: { label: "Pausado", tone: "neutro" },
  DELETED: { label: "Excluído", tone: "neutro" },
  ARCHIVED: { label: "Arquivado", tone: "neutro" },
  IN_PROCESS: { label: "Em processamento", tone: "atencao" },
  WITH_ISSUES: { label: "Com problemas", tone: "erro" },
  CAMPAIGN_PAUSED: { label: "Campanha pausada", tone: "neutro" },
  ADSET_PAUSED: { label: "Conjunto pausado", tone: "neutro" },
  PENDING_REVIEW: { label: "Em análise", tone: "atencao" },
  DISAPPROVED: { label: "Reprovado", tone: "erro" },
  PREAPPROVED: { label: "Pré-aprovado", tone: "atencao" },
  PENDING_BILLING_INFO: { label: "Aguardando pagamento", tone: "erro" },
};

export function effectiveStatusInfo(status: string | null | undefined): {
  label: string;
  tone: DeliveryTone;
} {
  if (!status) return { label: "Desconhecido", tone: "neutro" };
  return (
    (EFFECTIVE_STATUS_INFO as Record<string, { label: string; tone: DeliveryTone }>)[status] ?? {
      label: status,
      tone: "neutro",
    }
  );
}

/** Estratégias de lance (`bid_strategy`). */
export const BID_STRATEGY_LABELS: Record<string, string> = {
  LOWEST_COST_WITHOUT_CAP: "Menor custo (automático)",
  LOWEST_COST_WITH_BID_CAP: "Limite de lance",
  COST_CAP: "Limite de custo",
  LOWEST_COST_WITH_MIN_ROAS: "ROAS mínimo",
};

export function bidStrategyLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return BID_STRATEGY_LABELS[value] ?? value;
}

export const OPTIMIZATION_GOAL_LABELS: Record<string, string> = {
  OFFSITE_CONVERSIONS: "Conversões",
  LINK_CLICKS: "Cliques no link",
  LANDING_PAGE_VIEWS: "Visualizações da página de destino",
  IMPRESSIONS: "Impressões",
  REACH: "Alcance",
  LEAD_GENERATION: "Cadastros",
  QUALITY_LEAD: "Cadastros qualificados",
  CONVERSATIONS: "Conversas",
  THRUPLAY: "ThruPlay",
  POST_ENGAGEMENT: "Engajamento",
  APP_INSTALLS: "Instalações do app",
  VALUE: "Valor de conversão",
  QUALITY_CALL: "Ligações",
};

export function optimizationGoalLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return OPTIMIZATION_GOAL_LABELS[value] ?? value;
}

/** Status da fase de aprendizado do conjunto (`learning_stage_info.status`). */
export const LEARNING_STAGE_LABELS: Record<string, string> = {
  LEARNING: "Em aprendizado",
  SUCCESS: "Aprendizado concluído",
  FAIL: "Aprendizado limitado",
};

export const SPECIAL_AD_CATEGORY_LABELS: Record<string, string> = {
  NONE: "Nenhuma",
  HOUSING: "Moradia",
  EMPLOYMENT: "Emprego",
  CREDIT: "Crédito",
  ISSUES_ELECTIONS_POLITICS: "Temas sociais, eleições ou política",
  ONLINE_GAMBLING_AND_GAMING: "Jogos de azar on-line",
  FINANCIAL_PRODUCTS_SERVICES: "Produtos e serviços financeiros",
};

/** Opções do edge `/copies` da Meta. */
export const COPY_STATUS_OPTIONS = ["PAUSED", "ACTIVE", "INHERITED_FROM_SOURCE"] as const;
export type CopyStatusOption = (typeof COPY_STATUS_OPTIONS)[number];

/** Links oficiais (nunca oferecemos contorno de bloqueio; só o caminho de revisão da Meta). */
export const META_ACCOUNT_QUALITY_URL = "https://business.facebook.com/accountquality";
export function adsManagerUrl(adAccountMetaId: string, level: CampaignLevel, metaId: string) {
  const act = adAccountMetaId.replace(/^act_/, "");
  const page = level === "campaigns" ? "campaigns" : level === "adsets" ? "adsets" : "ads";
  const param =
    level === "campaigns"
      ? "selected_campaign_ids"
      : level === "adsets"
        ? "selected_adset_ids"
        : "selected_ad_ids";
  return `https://adsmanager.facebook.com/adsmanager/manage/${page}?act=${act}&${param}=${metaId}`;
}

// ---- Orçamento ------------------------------------------------------------------------------

export type BudgetKind = "diario" | "vitalicio";

/**
 * Orçamento diário mínimo por moeda, em centavos (menor unidade), para conjuntos otimizados por
 * impressões. A Meta expõe o valor exato por conta em `GET /act_{id}/minimum_budgets`; esta
 * tabela é a referência conservadora usada antes de mandar para a Meta (que ainda valida).
 */
export const MIN_DAILY_BUDGET_CENTS: Record<string, number> = {
  BRL: 500,
  USD: 100,
  EUR: 100,
  GBP: 100,
  ARS: 20000,
  CLP: 100000,
  COP: 500000,
  MXN: 2000,
  PEN: 400,
};
const DEFAULT_MIN_DAILY_BUDGET_CENTS = 100;

export function minDailyBudgetCents(currency: string): number {
  return MIN_DAILY_BUDGET_CENTS[currency] ?? DEFAULT_MIN_DAILY_BUDGET_CENTS;
}

/** Variação máxima aceita numa mudança percentual. */
export const BUDGET_PERCENT_MIN = -90;
export const BUDGET_PERCENT_MAX = 1000;
/** Acima disto a interface pede confirmação (regra da Fase 4: mudança > 50%). */
export const BUDGET_CONFIRM_RATIO = 0.5;

export interface BudgetChange {
  /** `absoluto` = valor final em centavos; `percentual` = variação em %. */
  mode: "absoluto" | "percentual";
  value: number;
}

/** Aplica uma mudança (valor ou %) a um orçamento atual em centavos. Função pura. */
export function applyBudgetChange(currentCents: number, change: BudgetChange): number {
  if (change.mode === "absoluto") return Math.round(change.value);
  return Math.round(currentCents * (1 + change.value / 100));
}

/** Verdadeiro se a mudança passa do limiar de confirmação (|Δ| > 50% do atual). */
export function budgetChangeNeedsConfirmation(currentCents: number, newCents: number): boolean {
  if (currentCents <= 0) return newCents > 0;
  return Math.abs(newCents - currentCents) / currentCents > BUDGET_CONFIRM_RATIO;
}

/** Valida um orçamento final (centavos) para a moeda. Devolve a mensagem de erro ou null. */
export function validateBudgetCents(
  cents: number,
  currency: string,
  kind: BudgetKind,
): string | null {
  if (!Number.isInteger(cents)) return "O orçamento precisa ser um valor inteiro em centavos.";
  if (cents <= 0) return "O orçamento precisa ser maior que zero.";
  const min = minDailyBudgetCents(currency);
  if (kind === "diario" && cents < min) {
    return `O orçamento diário mínimo para ${currency} é ${formatMinor(min, currency)}.`;
  }
  if (kind === "vitalicio" && cents < min) {
    return `O orçamento vitalício mínimo para ${currency} é ${formatMinor(min, currency)}.`;
  }
  if (cents > 1_000_000_000_000) return "Orçamento acima do limite aceito.";
  return null;
}

function formatMinor(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/**
 * Decide qual orçamento (diário ou vitalício) um item usa. A Meta não deixa misturar: quem tem
 * diário só muda diário; quem tem vitalício só muda vitalício; quem não tem nenhum (campanha ABO
 * ou conjunto em campanha CBO) não aceita orçamento neste nível.
 */
export function budgetKindOf(entity: {
  dailyBudgetCents: number | null;
  lifetimeBudgetCents: number | null;
}): BudgetKind | null {
  if (entity.dailyBudgetCents !== null && entity.dailyBudgetCents > 0) return "diario";
  if (entity.lifetimeBudgetCents !== null && entity.lifetimeBudgetCents > 0) return "vitalicio";
  return null;
}

// ---- Colunas da tabela -------------------------------------------------------------------------

export interface ColumnDef {
  id: string;
  label: string;
  /** Em quais abas a coluna existe. */
  levels: readonly CampaignLevel[];
  /** Visível por padrão. */
  default: boolean;
}

/** Todas as colunas configuráveis; `name` e `status` são fixas (não podem ser ocultadas). */
export const CAMPAIGN_TABLE_COLUMNS: readonly ColumnDef[] = [
  { id: "status", label: "Ativo", levels: CAMPAIGN_LEVELS, default: true },
  { id: "name", label: "Nome", levels: CAMPAIGN_LEVELS, default: true },
  { id: "delivery", label: "Veiculação", levels: CAMPAIGN_LEVELS, default: true },
  { id: "objective", label: "Objetivo", levels: ["campaigns"], default: true },
  { id: "campaign", label: "Campanha", levels: ["adsets", "ads"], default: true },
  { id: "adset", label: "Conjunto", levels: ["ads"], default: true },
  { id: "budget", label: "Orçamento", levels: ["campaigns", "adsets"], default: true },
  {
    id: "bidStrategy",
    label: "Estratégia de lance",
    levels: ["campaigns", "adsets"],
    default: false,
  },
  { id: "optimizationGoal", label: "Otimização", levels: ["adsets"], default: true },
  { id: "learning", label: "Aprendizado", levels: ["adsets"], default: false },
  { id: "creative", label: "Criativo", levels: ["ads"], default: true },
  { id: "schedule", label: "Período", levels: CAMPAIGN_LEVELS, default: false },
  { id: "createdAt", label: "Criado em", levels: CAMPAIGN_LEVELS, default: false },
  { id: "updatedAt", label: "Atualizado em", levels: CAMPAIGN_LEVELS, default: true },
  { id: "metaId", label: "ID", levels: CAMPAIGN_LEVELS, default: false },
  // Fase 6: métricas do período (somas e médias ponderadas do espelho de insights).
  ...METRIC_DEFS.map((m) => ({
    id: metricColumnId(m.id),
    label: m.label,
    levels: CAMPAIGN_LEVELS,
    default: METRIC_DEFAULT_COLUMNS.includes(m.id),
  })),
];

export const FIXED_COLUMN_IDS: readonly string[] = ["status", "name"];

export function defaultColumnsFor(level: CampaignLevel): string[] {
  return CAMPAIGN_TABLE_COLUMNS.filter((c) => c.levels.includes(level) && c.default).map(
    (c) => c.id,
  );
}

/** Limpa uma preferência salva: só colunas válidas para a aba, fixas sempre presentes e na frente. */
export function normalizeColumns(level: CampaignLevel, ids: readonly string[]): string[] {
  const valid = new Set(
    CAMPAIGN_TABLE_COLUMNS.filter((c) => c.levels.includes(level)).map((c) => c.id),
  );
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (!valid.has(id) || seen.has(id) || FIXED_COLUMN_IDS.includes(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return [...FIXED_COLUMN_IDS, ...out];
}

// ---- Período -------------------------------------------------------------------------------------

export const PERIOD_PRESETS = [
  "hoje",
  "ontem",
  "7d",
  "14d",
  "30d",
  "este_mes",
  "mes_passado",
  "maximo",
] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  hoje: "Hoje",
  ontem: "Ontem",
  "7d": "Últimos 7 dias",
  "14d": "Últimos 14 dias",
  "30d": "Últimos 30 dias",
  este_mes: "Este mês",
  mes_passado: "Mês passado",
  maximo: "Todo o período",
};

export interface DateRange {
  /** `AAAA-MM-DD` inclusivo, ou null (sem limite). */
  from: string | null;
  to: string | null;
}

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Converte um preset em intervalo de datas (no fuso local de quem chama). Função pura dado `today`. */
export function presetToRange(preset: PeriodPreset, today = new Date()): DateRange {
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysAgo = (n: number) => new Date(t.getFullYear(), t.getMonth(), t.getDate() - n);
  switch (preset) {
    case "hoje":
      return { from: iso(t), to: iso(t) };
    case "ontem":
      return { from: iso(daysAgo(1)), to: iso(daysAgo(1)) };
    case "7d":
      return { from: iso(daysAgo(6)), to: iso(t) };
    case "14d":
      return { from: iso(daysAgo(13)), to: iso(t) };
    case "30d":
      return { from: iso(daysAgo(29)), to: iso(t) };
    case "este_mes":
      return { from: iso(new Date(t.getFullYear(), t.getMonth(), 1)), to: iso(t) };
    case "mes_passado": {
      const first = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const last = new Date(t.getFullYear(), t.getMonth(), 0);
      return { from: iso(first), to: iso(last) };
    }
    case "maximo":
      return { from: null, to: null };
  }
}
